import { useEffect, useState } from "react";

import KeyIcon from "@mui/icons-material/Key";
import MailIcon from "@mui/icons-material/Mail";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import {
  Alert,
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { startAuthentication } from "@simplewebauthn/browser";

export interface ChainState {
  status: "factor_required" | "chain_complete";
  token: string;
  satisfied: string[];
  next_factors: string[];
}

// Where the in-flight chain token waits while the browser is away at Duo.
export const DUO_CHAIN_KEY = "janus.duo.chain";

const LABELS: Record<string, string> = {
  password: "Password",
  webauthn: "Security key",
  duo: "Duo",
  "email-otp": "Emailed code",
};

function factorIcon(factor: string) {
  if (factor === "webauthn") return <KeyIcon />;
  if (factor === "duo") return <PhoneIphoneIcon />;
  return <MailIcon />;
}

export default function FactorStep(props: {
  chain: ChainState;
  onBegin: (factor: string) => Promise<any>;
  onVerify: (factor: string, response: any) => Promise<void>;
  error: string;
  busy: boolean;
}) {
  const { chain, onBegin, onVerify, error, busy } = props;
  const factor = chain.next_factors[0];

  const [code, setCode] = useState("");
  const [started, setStarted] = useState(false);

  useEffect(() => {
    setCode("");
    setStarted(false);
  }, [factor, chain.token]);

  const begin = async () => {
    await onBegin(factor);
    setStarted(true);
  };

  const submitCode = async () => {
    await onVerify(factor, { code });
  };

  const submitSecurityKey = async () => {
    const options = await onBegin(factor);
    if (!options) return;
    const credential = await startAuthentication({ optionsJSON: options });
    await onVerify(factor, { credential });
  };

  // Duo leaves the site entirely, so the chain has to survive the round trip
  // through their servers. Session storage is the right scope: this tab only,
  // cleared when it closes.
  const submitDuo = async () => {
    const payload = await onBegin(factor);
    if (!payload?.redirect_url) return;
    sessionStorage.setItem(DUO_CHAIN_KEY, chain.token);
    window.location.assign(payload.redirect_url);
  };

  return (
    <Stack spacing={2} sx={{ flexGrow: 1 }}>
      <Box>
        <Typography variant="h6">One more step</Typography>
        <Typography variant="body2" color="text.secondary">
          {chain.satisfied.map((f) => LABELS[f] ?? f).join(", ")} accepted.
          Complete {LABELS[factor] ?? factor} to finish signing in.
        </Typography>
      </Box>

      <Divider />

      {error && <Alert severity="error">{error}</Alert>}

      {factor === "email-otp" && (
        <>
          {!started ? (
            <Button
              variant="contained"
              startIcon={<MailIcon />}
              onClick={begin}
              disabled={busy}
            >
              Send me a code
            </Button>
          ) : (
            <>
              <TextField
                label="Emailed code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputProps={{ inputMode: "numeric", maxLength: 6 }}
                autoFocus
              />
              <Button
                variant="contained"
                onClick={submitCode}
                disabled={busy || code.length === 0}
              >
                Verify
              </Button>
              <Button onClick={begin} disabled={busy}>
                Send another code
              </Button>
            </>
          )}
        </>
      )}

      {factor === "webauthn" && (
        <Button
          variant="contained"
          startIcon={<KeyIcon />}
          onClick={submitSecurityKey}
          disabled={busy}
        >
          Use security key
        </Button>
      )}

      {factor === "duo" && (
        <Button
          variant="contained"
          startIcon={<PhoneIphoneIcon />}
          onClick={submitDuo}
          disabled={busy}
        >
          Continue with Duo
        </Button>
      )}

      {factor !== "email-otp" && factor !== "webauthn" && factor !== "duo" && (
        <Button
          variant="contained"
          startIcon={factorIcon(factor)}
          onClick={begin}
          disabled={busy}
        >
          Continue with {LABELS[factor] ?? factor}
        </Button>
      )}
    </Stack>
  );
}
